import { useGetListGeneralParamsQuery } from '@api/genpar.api.rtk';
import { useAppDispatch, useAppSelector } from '@store/index';
import { clearGenpar, IGenpar } from '@store/slices/genpar.slice';
import { decrypt } from '@utils/security';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const useGenpar = ({
  startLoading,
  stopLoading,
}: {
  startLoading?: () => void;
  stopLoading?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const currentSegment = pathname?.split('/')?.[1];
  const property = useAppSelector(
    (state) => state.properties.selectedProperty.value,
  );
  const genparData = useAppSelector((state) => state.genpar);
  const { refetch, isLoading, isFetching } = useGetListGeneralParamsQuery(
    property,
    {
      refetchOnMountOrArgChange: true,
      skip:
        property === 'All Properties' ||
        currentSegment === 'login' ||
        !property,
    },
  );
  const skipFetchingPaths = ['/front-office/rates-and-availability'];

  const skipFetching = skipFetchingPaths.some((path) =>
    pathname.includes(path),
  );

  useEffect(() => {
    const loading = skipFetching ? isLoading : isLoading || isFetching;

    const timeout = setTimeout(() => {
      if (loading) {
        startLoading?.();
      } else {
        stopLoading?.();
      }
    }, 150);

    return () => clearTimeout(timeout);
  }, [isLoading, isFetching, skipFetching]);

  const clearGenparState = () => {
    dispatch(clearGenpar());
  };

  const findGenparByKey = (key: string): IGenpar | null => {
    return genparData.find((item) => item.parameter === key) ?? null;
  };

  const getGenparValue = (key: string, subModuleCode?: string): string => {
    // if subModuleCode exists, filter by both key and subModule
    if (subModuleCode) {
      const findBySubModuleCode = genparData.find(
        (item) => item.parameter === key && item.subModule === subModuleCode,
      );
      return findBySubModuleCode?.value
        ? decrypt(findBySubModuleCode.value)
        : '';
    }
    const item = findGenparByKey(key);
    return item?.value ? decrypt(item.value) : 'Invalid Genpar parameter';
  };

  const getGenparValueNoSubModule = (key: string): string => {
    // if subModuleCode exists, filter by both key and subModule
    const findBySubModuleCode = genparData.find(
      (item) => item.parameter === key && item.subModule === '',
    );
    return findBySubModuleCode?.value ? decrypt(findBySubModuleCode.value) : '';
  };

  return {
    genparData,
    clearGenparState,
    getGenparValue,
    refetchGenpar: refetch,
    getGenparValueNoSubModule,
  };
};

export default useGenpar;
